<template>
  <v-sheet height="100%" color="bg1" width="100%">
    <v-sheet height="100%" class="rounded-tl-lg overflow-hidden pa-4">
      <div
        class="pa-2 d-flex align-center"
        style="gap: 8px"
        :class="{
          'flex-wrap': $vuetify.breakpoint.xs,
        }">
        <h3>Manage Models</h3>
        <template v-has="permissions.Admin3DModels.Upload">
          <v-btn
            @click="showUploadForm()"
            small
            color="primary"
            v-has="permissions.Admin3DModels.Upload"
            ><v-icon left>mdi-upload</v-icon> Upload
          </v-btn>
          <v-btn
            v-if="canAddEmptyModel"
            @click="addEmptyModel()"
            small
            v-has="permissions.Admin3DModels.Upload"
            ><v-icon left>mdi-plus</v-icon> Add Empty Model
          </v-btn>
          <!-- <v-btn v-if="viewer" small color="primary" @click="collectObjectData()">
            <v-icon left>mdi-apache-kafka</v-icon>
            Collect Missed Data
          </v-btn>
          <v-btn v-if="viewer" small color="warning" @click="syncDbIds()">
            <v-icon left>mdi-sync</v-icon>
            Sync New Version
          </v-btn> -->
        </template>
      </div>
      <v-data-table
        class="elevation-0 pa-2 rounded-0 d-flex flex-column table-sticky custom-table"
        hide-default-footer
        disable-pagination
        :headers="headers"
        :items="masters"
        :loading="loading"
        @click:row="clickOnMasterModel">
        <template v-slot:[`item.name`]="{ item }">
          <h4 class="d-flex align-center">
            <v-checkbox
              small
              hide-details
              dense
              @change="mainVersionCheckboxChecked()"
              @click.stop=""
              class="pt-0 mt-0"
              v-model="selectedModelId"
              :value="item.id"
              v-has="permissions.Admin3DModels.Upload"></v-checkbox>
            {{ item.name }}
            <v-chip small class="ml-3" v-if="item.version" color="primary" outlined>
              v{{ item.version }}
            </v-chip>
            <v-chip small label class="ml-3" v-if="item.isSvf2" color="warning">
              <span>SVF2</span>
            </v-chip>
            <v-chip
              small
              label
              class="ml-2"
              :color="item.apsRegion === 2 ? 'blue-grey darken-1' : 'teal darken-1'"
              dark
            >
              <v-icon x-small left>mdi-earth</v-icon>
              {{ item.apsRegion === 2 ? 'EMEA' : 'US' }}
            </v-chip>
          </h4>
        </template>

        <!-- Translation Status Column -->
        <template v-slot:[`item.translationStatus`]="{ item }">
          <div class="translation-status-cell">
            <!-- Success State -->
            <template v-if="getTranslationStatus(item.id).status === 'success'">
              <v-chip small color="success" class="translation-chip translation-chip--success">
                <v-icon small left>mdi-check-circle</v-icon>
                Ready
              </v-chip>
            </template>

            <!-- In Progress State -->
            <template v-else-if="getTranslationStatus(item.id).status === 'inprogress'">
              <div class="translation-progress">
                <div class="progress-header">
                  <v-icon small color="info" class="rotating-icon">mdi-sync</v-icon>
                  <span class="progress-label">Translating...</span>
                  <span class="progress-percent">{{ getTranslationStatus(item.id).progressPercent }}%</span>
                </div>
                <v-progress-linear
                  :value="getTranslationStatus(item.id).progressPercent"
                  color="info"
                  height="6"
                  rounded
                  class="translation-progress-bar"
                  striped
                ></v-progress-linear>
              </div>
            </template>

            <!-- Pending State -->
            <template v-else-if="getTranslationStatus(item.id).status === 'pending'">
              <v-chip small color="warning" outlined class="translation-chip translation-chip--pending">
                <v-icon small left>mdi-clock-outline</v-icon>
                Queued
              </v-chip>
            </template>

            <!-- Failed State -->
            <template v-else-if="getTranslationStatus(item.id).status === 'failed'">
              <v-tooltip bottom max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-chip small color="error" v-bind="attrs" v-on="on" class="translation-chip translation-chip--error">
                    <v-icon small left>mdi-alert-circle</v-icon>
                    Failed
                  </v-chip>
                </template>
                <span>{{ getTranslationStatus(item.id).errorMessage || 'Translation failed. Try re-translating the model.' }}</span>
              </v-tooltip>
            </template>

            <!-- Timeout State -->
            <template v-else-if="getTranslationStatus(item.id).status === 'timeout'">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-chip small color="warning" v-bind="attrs" v-on="on" class="translation-chip translation-chip--timeout">
                    <v-icon small left>mdi-clock-alert</v-icon>
                    Timeout
                  </v-chip>
                </template>
                <span>Translation timed out. Try re-translating the model.</span>
              </v-tooltip>
            </template>

            <!-- Unknown/Loading State -->
            <template v-else>
              <v-chip small outlined class="translation-chip">
                <v-progress-circular
                  indeterminate
                  size="14"
                  width="2"
                  class="mr-2"
                ></v-progress-circular>
                Checking...
              </v-chip>
            </template>
          </div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu dense offset-x right>
            <template v-slot:activator="{ attrs, on }">
              <v-btn icon elevation="0" v-bind="attrs" v-on="on">
                <v-icon size="16px">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list class="more-options-menu" dense>
              <v-list-item
                v-has="permissions.Admin3DModels.Upload"
                @click="uploadNewVersion(item)"
                dense>
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="primary">mdi-upload</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Upload New Version</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                @click="manageModelVersions(item)"
                dense>
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="primary">mdi-cog-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Manage</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-has="permissions.Admin3DModels.Upload"
                @click="openTranslateDialog(item)"
                dense>
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="info">mdi-earth-arrow-right</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Translate to {{ getTargetRegionName(item) }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-has="permissions.Admin3DModels.Delete"
                @click="onItemDeleting(item)"
                dense>
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="error">mdi-delete-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template v-slot:no-data>
          <div class="text-center d-flex flex-column">
            <img width="300" src="/img/notFound.svg" class="py-5 my-8" style="opacity: 0.54" />
            <p class="font-weight-medium">No Data Available!</p>
          </div>
        </template>

        <template v-slot:loading>
          <div class="text-center d-flex flex-column">
            <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
            <p class="font-weight-medium">Loading Models...</p>
          </div>
        </template>
      </v-data-table>
    </v-sheet>
    <uploadModel ref="uploadModel" @uploadSuccess="onUploadSuccess"></uploadModel>
    
    <!-- Model Versions Management Dialog -->
    <ManageVersionsDialog
      v-model="manageVersionsDialog.show"
      :model-id="manageVersionsDialog.modelId"
      :model-name="manageVersionsDialog.modelName"
      :facility-id="$route.params.id"
      @model-updated="onModelUpdated"
    />
    
    <!-- Progress Dialog -->
    <v-dialog v-model="progressDialog.show" persistent max-width="400">
      <v-card>
        <v-card-title class="title grey--text">{{ progressDialog.title }}</v-card-title>
        <v-card-text class="pb-2">
          <p class="mb-1" v-if="progressDialog.total">Total: {{ progressDialog.total }}</p>
          <p class="mb-1" v-if="progressDialog.saved">Saved: {{ progressDialog.saved }}</p>
          <p class="mb-1">{{ progressDialog.message }}</p>
          <p class="mb-1" v-if="progressDialog.objectKey">Object: {{ progressDialog.objectKey }}</p>
          <p class="mb-1 warning--text">Please wait while we process the data...</p>
          <v-progress-linear
            :value="progressDialog.progress"
            class="rounded-pill animate" striped stream
            color="primary"
            height="25"
          >
            <template v-slot:default>
              {{ Math.ceil(progressDialog.progress) }}%
            </template>
          </v-progress-linear>
        </v-card-text>
        <v-card-actions v-if="progressDialog.canCancel">
          <v-spacer></v-spacer>
          <v-btn color="error" text @click="cancelOperation">
            Stop
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Translate to Region Dialog -->
    <v-dialog v-model="translateDialog.show" max-width="660">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="warning">mdi-earth-arrow-right</v-icon>
          Translate Model to {{ translateDialog.targetRegion }}
        </v-card-title>
        <v-card-text>
          <v-alert type="warning" dense class="mb-3">
            <strong>Important:</strong> This will delete the current translation and re-translate the model.
            The model will be temporarily unavailable during translation.
          </v-alert>
          <p class="mb-2">
            This will translate <strong>"{{ translateDialog.model?.name }}"</strong>
            from <strong>{{ translateDialog.sourceRegion }}</strong> to <strong>{{ translateDialog.targetRegion }}</strong>.
          </p>
          <p class="caption grey--text">
            Translation may take several minutes depending on model size.
            You can still use other features while the translation is in progress.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="translateDialog.show = false" :disabled="translateDialog.loading">
            Cancel
          </v-btn>
          <v-btn
            color="warning"
            :loading="translateDialog.loading"
            @click="confirmTranslate"
          >
            <v-icon left>mdi-earth-arrow-right</v-icon>
            Translate
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script>
  import { checkIsModelTrasnlated, getByNameAsync, getByPropertyAsync } from '@/utils/forgeUtils';
  import { assertPersistableUnitScale } from '@/utils/coordinateAuthority';
  import HttpService from '@/utils/httpService';
  import permissions from '@Share/config/permissions';
  import headers from '@Admin/config/models.headers';
  import uploadModel from '../components/uploadModel.vue';
  import ManageVersionsDialog from '../components/ManageVersionsDialog.vue';
  import httpService from '@/utils/httpService';
  import dbId2box from '@/extensions/dbId2box';
  import { mapActions, mapGetters } from 'vuex';
  import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

  export default {
    name: 'AdminModels',
    props: ['modelsLoading'],
    data() {
      return {
        permissions,
        isExpanded: false,
        selectedModelId: '',
        headers,
        cached: new Map(),
        progressDialog: {
          show: false,
          title: '',
          message: '',
          objectKey: '',
          total: 0,
          saved: 0,
          progress: 0,
          canCancel: false,
          cancelled: false
        },
        manageVersionsDialog: {
          show: false,
          modelId: null,
          modelName: ''
        },
        translateDialog: {
          show: false,
          model: null,
          sourceRegion: '',
          targetRegion: '',
          loading: false
        },
        // Translation status tracking
        translationStatuses: {}, // Map of modelId -> { status, progress, progressPercent, errorMessage }
        translationPollingInterval: null,
        POLLING_INTERVAL_MS: 8000, // Poll every 8 seconds
      };
    },
    watch: {
      masters: {
        handler: function (newMasters, oldMasters) {
          this.selectedModelId = this.masters.find((o) => o.isMainVersion)
            ? this.masters.find((o) => o.isMainVersion).id
            : '';

          // Restart polling when masters list changes (new model added, etc.)
          if (newMasters?.length !== oldMasters?.length) {
            this.startTranslationPolling();
          }
        },
        immediate: true,
      },
    },
    methods: {
      ...mapActions('ADMIN_MODELS', ['GET_MASTERS']),

      // ═══════════════════════════════════════════════════════════════════
      // Translation Status Methods
      // ═══════════════════════════════════════════════════════════════════

      /**
       * Get translation status for a model
       * Returns a default object if status hasn't been fetched yet
       * @param {string} modelId - The model ID
       * @returns {Object} Translation status object
       */
      getTranslationStatus(modelId) {
        return this.translationStatuses[modelId] || {
          status: null,
          progress: '',
          progressPercent: 0,
          errorMessage: null,
        };
      },

      /**
       * Fetch translation status for a single model from the backend
       * Backend should call Autodesk's GET /modelderivative/v2/designdata/{urn}/manifest
       * @param {Object} model - The model object with id and urn
       */
      async fetchTranslationStatus(model) {
        if (!model?.id || !model?.urn) return;

        try {
          const response = await httpService.get({
            url: `api/models/${model.id}/translation-status`,
          });

          const statusData = response.data;

          // Use Vue.set for reactivity
          this.$set(this.translationStatuses, model.id, {
            status: statusData.status,
            progress: statusData.progress,
            progressPercent: statusData.progressPercent || this.parseProgressPercent(statusData.progress),
            errorMessage: statusData.errorMessage,
            hasThumbnail: statusData.hasThumbnail,
            lastChecked: new Date(),
          });
        } catch (err) {
          debugError(`Error fetching translation status for model ${model.id}:`, err);
          // Set error state but don't overwrite existing successful state
          if (!this.translationStatuses[model.id]?.status) {
            this.$set(this.translationStatuses, model.id, {
              status: 'unknown',
              progress: '',
              progressPercent: 0,
              errorMessage: 'Unable to fetch status',
            });
          }
        }
      },

      /**
       * Parse progress percentage from Autodesk's "XX% complete" string
       * @param {string} progressString - e.g., "50% complete"
       * @returns {number} Percentage as integer
       */
      parseProgressPercent(progressString) {
        if (!progressString) return 0;
        const match = progressString.match(/(\d+)%/);
        return match ? parseInt(match[1], 10) : 0;
      },

      /**
       * Fetch translation statuses for all models
       * Called on mount and periodically while polling
       */
      async fetchAllTranslationStatuses() {
        if (!this.masters?.length) return;

        // Fetch in parallel for all models
        const promises = this.masters.map(model => this.fetchTranslationStatus(model));
        await Promise.allSettled(promises);
      },

      /**
       * Start polling for translation status updates
       * Polls every POLLING_INTERVAL_MS for models that are still translating
       */
      startTranslationPolling() {
        // Clear any existing interval
        this.stopTranslationPolling();

        // Initial fetch
        this.fetchAllTranslationStatuses();

        // Set up polling interval
        this.translationPollingInterval = setInterval(() => {
          // Only poll if there are models still translating
          const hasTranslatingModels = this.masters?.some(model => {
            const status = this.translationStatuses[model.id]?.status;
            return status === 'inprogress' || status === 'pending' || !status;
          });

          if (hasTranslatingModels) {
            this.fetchAllTranslationStatuses();
          } else {
            // All models are done, stop polling
            this.stopTranslationPolling();
          }
        }, this.POLLING_INTERVAL_MS);
      },

      /**
       * Stop the translation status polling
       */
      stopTranslationPolling() {
        if (this.translationPollingInterval) {
          clearInterval(this.translationPollingInterval);
          this.translationPollingInterval = null;
        }
      },

      /**
       * Manually refresh translation status for a specific model
       * Useful after triggering a new translation
       * @param {string} modelId - The model ID to refresh
       */
      async refreshModelTranslationStatus(modelId) {
        const model = this.masters?.find(m => m.id === modelId);
        if (model) {
          // Set to pending immediately to show visual feedback
          this.$set(this.translationStatuses, modelId, {
            status: 'pending',
            progress: 'Starting...',
            progressPercent: 0,
            errorMessage: null,
          });

          // Start polling if not already running
          if (!this.translationPollingInterval) {
            this.startTranslationPolling();
          }
        }
      },

      // ═══════════════════════════════════════════════════════════════════
      // End Translation Status Methods
      // ═══════════════════════════════════════════════════════════════════

      async syncDbIds() {
        this.cached.clear();
        
        // Initialize progress dialog
        this.progressDialog = {
          show: true,
          title: 'Syncing DbIds',
          message: 'Preparing to sync...',
          progress: 0,
          canCancel: true,
          cancelled: false
        };

        let searchQuery = {
          search: '',
          page: 0,
          itemsPerPage: 1000,
        };

        try {
          let count = 0;
          let processed = 0;
          
          // First get the total count to calculate progress
          const { total } = await this.getObjects3D(searchQuery);
          count = total;
          
          this.progressDialog.total = count;

          if (count === 0) {
            this.progressDialog.show = false;
            this.$dialog.notify.info('No objects found to sync.', {
              position: 'top-right',
              timeout: 3000,
            });
            return;
          }
          
          searchQuery.page = 0;
          
          do {
            if (this.progressDialog.cancelled) {
              this.$dialog.notify.info('Sync operation cancelled.', {
                position: 'top-right',
                timeout: 3000,
              });
              break;
            }
            
            const { items } = await this.getObjects3D(searchQuery);
            if (items.length === 0) break;
            
            this.progressDialog.message = `Processing batch ${searchQuery.page + 1}...`;
            
            await this.syncBatchDbIds(items);
            
            processed += items.length;

            this.progressDialog.saved = processed;

            searchQuery.page++;
            
            // Update progress percentage
            this.progressDialog.progress = (processed / count) * 100;
          } while (processed < count);
          
          if (!this.progressDialog.cancelled) {
            this.$dialog.notify.success('Sync completed successfully.', {
              position: 'top-right',
              timeout: 3000,
            });
          }
        } catch (error) {
          debugError('Error during sync:', error);
          this.$dialog.notify.error('Error during sync operation.', {
            position: 'top-right',
            timeout: 3000,
          });
        } finally {
          this.progressDialog.show = false;
        }
      },
      
      async collectObjectData() {
        // Initialize progress dialog
        this.progressDialog = {
          show: true,
          title: 'Collecting Object Data',
          message: 'Preparing to collect data...',
          progress: 0,
          canCancel: true,
          cancelled: false
        };
        
        let searchQuery = {
          search: '',
          page: 0,
          itemsPerPage: 1000,
        };

        try {
          let count = 0;
          let processed = 0;
          
          // First get the total count to calculate progress
          const { total } = await this.getObjects3D(searchQuery);
          count = total;

          this.progressDialog.total = count;
          
          if (count === 0) {
            this.progressDialog.show = false;
            this.$dialog.notify.info('No objects found to collect data from.', {
              position: 'top-right',
              timeout: 3000,
            });
            return;
          }
          
          searchQuery.page = 0;
          
          do {
            if (this.progressDialog.cancelled) {
              this.$dialog.notify.info('Collection operation cancelled.', {
                position: 'top-right',
                timeout: 3000,
              });
              break;
            }
            
            const { items } = await this.getObjects3D(searchQuery);
            if (items.length === 0) break;
            
            this.progressDialog.message = `Processing batch ${searchQuery.page + 1} of objects...`;
            
            await this.collectBatchObjectData(items);
            
            processed += items.length;

            this.progressDialog.saved = processed;

            searchQuery.page++;
            
            // Update progress percentage
            this.progressDialog.progress = (processed / count) * 100;
          } while (processed < count);
          
          if (!this.progressDialog.cancelled) {
            this.$dialog.notify.success('Data collection completed successfully.', {
              position: 'top-right',
              timeout: 3000,
            });
          }
        } catch (error) {
          debugError('Error during data collection:', error);
          this.$dialog.notify.error('Error during data collection operation.', {
            position: 'top-right',
            timeout: 3000,
          });
        } finally {
          this.progressDialog.show = false;
        }
      },
      
      cancelOperation() {
        this.progressDialog.cancelled = true;
        this.progressDialog.message = "Cancelling operation...";
      },
      
      async syncBatchDbIds(objects) {
        const TOLERANCE = 0.01;

        function isEqual(a, b) {
          return Math.abs(a - b) < TOLERANCE;
        }
        function isEqualBox(box1, box2) {
          return (
            isEqual(box1.min.x, box2.min.x) &&
            isEqual(box1.min.y, box2.min.y) &&
            isEqual(box1.min.z, box2.min.z) &&
            isEqual(box1.max.x, box2.max.x) &&
            isEqual(box1.max.y, box2.max.y) &&
            isEqual(box1.max.z, box2.max.z)
          );
        }

        const globalOffset = this.viewer.model.getGlobalOffset();
        // Authority scale, never `getUnitScale() || 1.0` — the silent 1.0 fallback
        // mis-converted stored (global) min/max on mis-declared models.
        let unitScale;
        try {
          unitScale = assertPersistableUnitScale(this.viewer.model).scale;
        } catch (err) {
          debugError('[Models] Unit scale unavailable — aborting object matching:', err.message);
          this.$dialog.notify.error(err.message, { position: 'top-right', timeout: 5000 });
          return;
        }

        for (let i = 0; i < objects.length; i++) {
          if (this.progressDialog.cancelled) return;

          let object = objects[i];
          let key = object.moniker || object.value || object.name;
          
          // Update detailed progress message
          this.progressDialog.message = `Processing object ${i+1}/${objects.length}`;
          this.progressDialog.objectKey = object.name;

          if (!object.min || !object.max) continue;

          const min = new window.THREE.Vector3(object.min.x, object.min.y, object.min.z);
          const max = new window.THREE.Vector3(object.max.x, object.max.y, object.max.z);
          min.multiplyScalar(1 / unitScale).sub(globalOffset);
          max.multiplyScalar(1 / unitScale).sub(globalOffset);

          const box = new window.THREE.Box3(min, max);

          let dbIds = [];

          if (this.cached.has(key)) {
            dbIds = this.cached.get(key);
          } else {
            dbIds = await getByPropertyAsync(this.viewer.model, 'name', key);
            this.cached.set(key, dbIds);
          }

          if (dbIds.length > 0) {
            let updated = false;
            // Get bbox until found element that matches the stored bounding box
            for (let j = 0; j < dbIds.length; j++) {
              let dbId = dbIds[j];
              let bbox = dbId2box.computeBBox(this.viewer.model, dbId);
              if (isEqualBox(bbox, box)) {
                object.dbId = dbId;
                updated = true;
                break;
              }
            }

            if (!updated) {
              debugWarn(`No matching dbId found for object: ${object.name}`);
            }
          } else {
            debugWarn(`No dbId found for object: ${object.name}`);
          }
        }

        this.saveObjects3D(objects)
          .catch((error) => {
            debugLog(`Error saving objects batch: ${error}`);
          });
      },
      async collectBatchObjectData(objects) {
        const globalOffset = this.viewer.model.getGlobalOffset();
        // Authority scale, never `getUnitScale() || 1.0` — that fallback persisted
        // raw-model Min/Max for mis-declared models (same bug class as Center).
        let unitScale;
        try {
          unitScale = assertPersistableUnitScale(this.viewer.model).scale;
        } catch (err) {
          debugError('[Models] Unit scale unavailable — aborting data collection:', err.message);
          this.$dialog.notify.error(err.message, { position: 'top-right', timeout: 5000 });
          return;
        }

        for (let i = 0; i < objects.length; i++) {
          if (this.progressDialog.cancelled) return;
          
          let object = objects[i];
          
          // Update detailed progress message
          this.progressDialog.message = `Processing object ${i+1}/${objects.length}`;
          this.progressDialog.objectKey = object.name;

          let bbox = dbId2box.computeBBox(this.viewer.model, object.dbId);
          object.min = bbox.min.add(globalOffset).multiplyScalar(unitScale);
          object.max = bbox.max.add(globalOffset).multiplyScalar(unitScale);
        }

        this.saveObjects3D(objects)
          .catch((error) => {
            debugLog(`Error saving objects batch: ${error}`);
          });
      },
      async getObjects3D(searchQuery) {
        return new Promise((resolve, reject) => {
          httpService
            .get({
              url: `api/objects3d/${this.$route.params.id}/getObjects3D`,
              params: searchQuery,
            })
            .then((response) => {
              resolve(response.data);
            })
            .catch((error) => {
              debugError(error);
              reject(error);
            });
        });
      },
      async filterDbIdsByName(name) {
        let dbIds = await getByNameAsync(this.viewer, name);
        if (dbIds.length > 0) {
          return dbIds;
        } else {
          debugLog(`No dbId found for object: ${name}`);
          return [];
        }
      },
      async saveObjects3D(objects) {
        return new Promise((resolve, reject) => {
          httpService
            .put({ url: `api/objects3d/${this.$route.params.id}/saveObjects3D`, data: objects })
            .then((response) => {
              resolve(response.data);
            })
            .catch((error) => {
              debugError(error);
              reject(error);
            });
        });
      },
      manageModelVersions(model) {
        this.manageVersionsDialog.show = true;
        this.manageVersionsDialog.modelId = model.id;
        this.manageVersionsDialog.modelName = model.name;
      },
      onModelUpdated(updatedModel) {
        // Update the dialog with the new model information
        this.manageVersionsDialog.modelId = updatedModel.id;
        this.manageVersionsDialog.modelName = updatedModel.name;
      },
      showUploadForm() {
        this.$refs.uploadModel.open();
      },
      uploadNewVersion(model) {
        this.$refs.uploadModel.openForNewVersion(model);
      },
      addEmptyModel() {
        HttpService.post({
          url: `api/assets/addEmptyModel`,
          data: {
            assetId: this.$route.params.id,
            userUpdated: this.$store.state.username,
          },
        })
          .then((response) => {
            let model = response.data;
            this.masters.forEach((o) => (o.isMainVersion = false));
            model.isMainVersion = true;
            this.masters.push(model);

            this.$dialog.notify.success('Empty model created successfully.', {
              position: 'top-right',
              timeout: 3000,
            });
          })
          .catch(debugError);
      },
      mainVersionCheckboxChecked() {
        this.masters.forEach((o) => {
          if (o.id != this.selectedModelId) o.isMainVersion = false;
          else {
            o.isMainVersion = true;
            this.forgeService.saveMasterModel(this.$store.state.assetId, o.name);
          }
        });
      },
      clickOnMasterModel(model) {
        if (!model) return;
        var _this = this;
        checkIsModelTrasnlated(model.urn, model.apsRegion, (isTranslated, progress) => {
          if (!isTranslated) {
            this.$dialog.notify.warning('Model translation in progress.' + progress, {
              position: 'top-right',
              timeout: 3000,
            });
            return;
          } else {
            _this.$emit('launch-viewer', model);
          }
        });
      },
      onItemDeleting(model) {
        this.$dialog.error({
          text: `Are you sure you want to delete model?`,
          title: `Delete Model`,
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                if (this.getSelectedModel() == model) {
                  this.$emit('dispose3DViewer');
                }
                this.loading = true;
                return this.forgeService.deleteVersion(this.$route.params.id, model.id, () => {
                  this.GET_MASTERS().then(() => {
                    this.loading = false;
                    this.$dialog.notify.success('Model deleted successfully.', {
                      position: 'top-right',
                      timeout: 3000,
                    });
                  });
                });
              },
            },
          },
        });
      },
      getSelectedModel() {
        return this.masters.find((o) => o.isMainVersion);
      },
      onUploadSuccess() {
        this.loading = true;
        this.GET_MASTERS().then(() => {
          // Start polling to track the new upload's translation status
          this.startTranslationPolling();
        }).finally(() => {
          this.loading = false;
        });
      },
      getTargetRegionName(model) {
        return model.apsRegion === 2 ? 'US' : 'EMEA';
      },
      openTranslateDialog(model) {
        this.translateDialog = {
          show: true,
          model,
          sourceRegion: model.apsRegion === 2 ? 'EMEA' : 'US',
          targetRegion: model.apsRegion === 2 ? 'US' : 'EMEA',
          loading: false
        };
      },
      async confirmTranslate() {
        this.translateDialog.loading = true;
        const modelId = this.translateDialog.model.id;
        try {
          const targetRegion = this.translateDialog.model.apsRegion === 2 ? 1 : 2;
          await this.$store.dispatch('ADMIN_MODELS/TRANSLATE_TO_REGION', {
            assetId: this.$route.params.id,
            modelId: modelId,
            targetRegion
          });
          this.$dialog.notify.success(
            `Model translation to ${this.translateDialog.targetRegion} started successfully!`,
            { position: 'top-right', timeout: 4000 }
          );
          this.translateDialog.show = false;

          // Refresh the model's translation status and start polling
          this.refreshModelTranslationStatus(modelId);

          // Refresh the models list to show updated region
          this.GET_MASTERS();
        } catch (err) {
          this.$dialog.notify.error(
            'Translation failed: ' + (err.response?.data?.error || err.message),
            { position: 'top-right', timeout: 5000 }
          );
        } finally {
          this.translateDialog.loading = false;
        }
      },
    },
    computed: {
      ...mapGetters('ADMIN_MODELS', {
        masters: 'GET_ENTRIES',
        total: 'GET_TOTAL',
      }),


      loading: {
        get() {
          return this.$store.getters['ADMIN_MODELS/GET_LOADING'];
        },
        set(value) {
          this.$store.commit('ADMIN_MODELS/SET_LOADING', value);
        }
      },
      forgeService() {
        return this.$store.state.forgeService;
      },
      viewer() {
        return this.$store.state.viewer;
      },
      // models() {
      //   return this.$store.state.uploadedModels;
      // },
      canAddEmptyModel() {
        return this.masters.every((m) => m.name != 'Empty Model');
      },
    },
    mounted() {
      this.GET_MASTERS().then(() => {
        // Start polling for translation statuses after models are loaded
        this.startTranslationPolling();
      });
    },

    beforeDestroy() {
      // Clean up polling interval when component is destroyed
      this.stopTranslationPolling();
    },
    components: {
      uploadModel,
      ManageVersionsDialog,
    },
  };
</script>

<style lang="scss">
  .model-3d {
    background-size: cover;
    background-image: url(../../../images/3d-black.png);
    width: 23px;
    height: 23px;
    margin-right: 10px;
  }

  // ═══════════════════════════════════════════════════════════════════
  // Translation Status Styles
  // ═══════════════════════════════════════════════════════════════════

  .translation-status-cell {
    min-width: 140px;
    padding: 4px 0;
    
  }

  .translation-chip {
    font-weight: 500;
    letter-spacing: 0.02em;

    .v-icon {
      color: white !important;
      opacity: 0.9;
    }
  }

  .translation-progress {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 160px;
    max-width: 200px;

    .progress-header {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.85);

      .progress-label {
        flex: 1;
        font-weight: 500;
      }

      .progress-percent {
        font-weight: 600;
        color: var(--v-info-base, #2196F3);
        min-width: 36px;
        text-align: right;
      }
    }

    .translation-progress-bar {
      border-radius: 4px;
      overflow: hidden;

      // Striped animation
      &.v-progress-linear--striped {
        .v-progress-linear__determinate {
          animation: progress-stripe-animation 1s linear infinite;
        }
      }
    }
  }

  // Rotating sync icon animation
  .rotating-icon {
    animation: rotate-sync 1.5s linear infinite;
  }

  @keyframes rotate-sync {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes progress-stripe-animation {
    0% {
      background-position: 0 0;
    }
    100% {
      background-position: 40px 0;
    }
  }

  // Status-specific colors - icons are white by default from .translation-chip

  .translation-chip--error {
    cursor: help;
  }

  // Pulse animation for pending/queued items
  @keyframes pulse-opacity {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
  }

  .translation-chip--pending {
    animation: pulse-opacity 2s ease-in-out infinite;
  }
</style>
